import {
    getAdaptiveQualityConstraints,
    type ConnectOptions,
    type ConnectionQualityKey,
    type VideoConstraints,
    type VideoPresetKey,
} from '../config/mediaConfig'

export interface NetworkInformationLike {
    effectiveType?: string
    saveData?: boolean
    downlink?: number
    rtt?: number
}

export interface MeasureServerRttOptions {
    timeoutMs?: number
    fetchImpl?: typeof fetch
    now?: () => number
}

export interface LiveKitConnectionProfile {
    key: 'default' | 'low-bandwidth' | 'audio-priority'
    videoPreset: VideoPresetKey
    connectOptionsOverrides: Partial<ConnectOptions>
    startWithVideoDisabled: boolean
}

export interface AdaptiveCameraPlan {
    poorOrUnknownStreak: number
    constraints: VideoConstraints
    shouldDisableVideo: boolean
}

export interface AdaptiveCameraPlanInput {
    quality: ConnectionQualityKey
    poorOrUnknownStreak: number
    userPrefersVideo: boolean
    isVideoCurrentlyEnabled: boolean
    networkVideoSuppressed: boolean
    isScreenShareEnabled: boolean
}

export interface ReconnectDelayInput {
    attempt: number
    baseDelayMs: number
    maxDelayMs?: number
    jitterRatio?: number
    random?: () => number
}

export interface StartupMediaPlanInput {
    connectionProfile: Pick<LiveKitConnectionProfile, 'key' | 'startWithVideoDisabled'>
    measuredRttMs?: number | null
    userPrefersVideo: boolean
}

export interface StartupMediaPlan {
    keepVideoSuppressedUntilManual: boolean
    shouldDelayCameraStart: boolean
    cameraEnableDelayMs: number
    publishRetryDelaysMs: number[]
}

/**
 * Decide what to do when the camera-enable timer fires.
 *
 * Background: a regression in the LiveKit camera-publish path silently aborted
 * camera enabling whenever `room.state` momentarily wasn't 'connected' (e.g. a
 * brief reconnect right after join). Other participants then saw "Camera
 * disabled" forever even though the local UI still showed the camera as on.
 *
 * The decision is intentionally a pure function so the regression has direct
 * unit-test coverage instead of being buried inside the connector component.
 */
export type CameraEnableDecision =
    | { action: 'publish' }
    | { action: 'retry-later'; delayMs: number }
    | { action: 'abort'; reason: string }

export interface CameraEnableDecisionInput {
    /** Whether the room reference still matches the room that scheduled this attempt. */
    roomMatches: boolean
    /** Current LiveKit room state. */
    roomState:
    | 'connected'
    | 'connecting'
    | 'reconnecting'
    | 'disconnected'
    | 'disconnecting'
    | string
    /** Zero-based attempt index. */
    attempt: number
    /** Maximum attempts (after this, we abort instead of retrying). */
    maxAttempts: number
    /** Backoff used when waiting for a transient state to stabilise. */
    transientRetryDelayMs?: number
}

const DEFAULT_TRANSIENT_RETRY_DELAY_MS = 750

export function decideCameraEnableAction(
    input: CameraEnableDecisionInput
): CameraEnableDecision {
    if (!input.roomMatches) {
        return { action: 'abort', reason: 'room-replaced' }
    }

    if (input.roomState === 'connected') {
        return { action: 'publish' }
    }

    if (input.roomState === 'disconnected' || input.roomState === 'disconnecting') {
        return { action: 'abort', reason: `room-${input.roomState}` }
    }

    // 'connecting' / 'reconnecting' / unknown: retry instead of silently dropping.
    if (input.attempt >= input.maxAttempts) {
        return { action: 'abort', reason: 'transient-state-exhausted' }
    }

    return {
        action: 'retry-later',
        delayMs: input.transientRetryDelayMs ?? DEFAULT_TRANSIENT_RETRY_DELAY_MS,
    }
}

const AUDIO_PRIORITY_CONNECT_OVERRIDES: Partial<ConnectOptions> = {
    maxRetries: 6,
    peerConnectionTimeout: 35000,
    websocketTimeout: 35000,
}

const LOW_BANDWIDTH_CONNECT_OVERRIDES: Partial<ConnectOptions> = {
    maxRetries: 6,
    peerConnectionTimeout: 30000,
    websocketTimeout: 30000,
}

const DEFAULT_PUBLISH_RETRY_DELAYS_MS = [0, 1000, 2000]
const LOW_BANDWIDTH_PUBLISH_RETRY_DELAYS_MS = [0, 1500, 3000, 5000]
const LONG_HAUL_PUBLISH_RETRY_DELAYS_MS = [0, 1500, 3500, 6500]
const LONG_HAUL_AUDIO_FIRST_RTT_MS = 250
const LOW_BANDWIDTH_CAMERA_DELAY_MS = 1500
const LONG_HAUL_CAMERA_DELAY_MS = 2500

export function getNavigatorNetworkInformation(): NetworkInformationLike | null {
    if (typeof navigator === 'undefined') return null

    const nav = navigator as Navigator & {
        connection?: NetworkInformationLike
        mozConnection?: NetworkInformationLike
        webkitConnection?: NetworkInformationLike
    }

    return nav.connection ?? nav.mozConnection ?? nav.webkitConnection ?? null
}

export function selectLiveKitConnectionProfile(
    networkInfo?: NetworkInformationLike | null
): LiveKitConnectionProfile {
    const effectiveType = networkInfo?.effectiveType?.toLowerCase()
    const saveData = networkInfo?.saveData === true
    const downlink = networkInfo?.downlink ?? Number.POSITIVE_INFINITY
    const rtt = networkInfo?.rtt ?? 0
    const hasUnknownNetworkSignals =
        !networkInfo ||
        (
            networkInfo.effectiveType == null &&
            networkInfo.downlink == null &&
            networkInfo.rtt == null &&
            networkInfo.saveData !== true
        )

    const audioPriority =
        saveData ||
        effectiveType === 'slow-2g' ||
        effectiveType === '2g' ||
        downlink < 0.7 ||
        rtt >= 1400

    if (audioPriority) {
        return {
            key: 'audio-priority',
            videoPreset: 'h360',
            connectOptionsOverrides: AUDIO_PRIORITY_CONNECT_OVERRIDES,
            startWithVideoDisabled: true,
        }
    }

    if (hasUnknownNetworkSignals) {
        return {
            key: 'low-bandwidth',
            videoPreset: 'h360',
            connectOptionsOverrides: LOW_BANDWIDTH_CONNECT_OVERRIDES,
            startWithVideoDisabled: false,
        }
    }

    const lowBandwidth =
        effectiveType === '3g' ||
        downlink < 1.5 ||
        rtt >= 700

    if (lowBandwidth) {
        return {
            key: 'low-bandwidth',
            videoPreset: 'h360',
            connectOptionsOverrides: LOW_BANDWIDTH_CONNECT_OVERRIDES,
            startWithVideoDisabled: false,
        }
    }

    return {
        key: 'default',
        videoPreset: 'h360',
        connectOptionsOverrides: {},
        startWithVideoDisabled: false,
    }
}

export async function measureServerRtt(
    serverBaseUrl: string,
    options: MeasureServerRttOptions = {}
): Promise<number | null> {
    const fetchImpl = options.fetchImpl ?? (typeof fetch === 'function' ? fetch : undefined)
    if (!fetchImpl) return null

    const now = options.now ?? (() => {
        if (typeof performance !== 'undefined' && typeof performance.now === 'function') {
            return performance.now()
        }
        return Date.now()
    })

    const timeoutMs = options.timeoutMs ?? 2500
    const controller = typeof AbortController !== 'undefined' ? new AbortController() : null
    const endpoint = new URL('/health', serverBaseUrl).toString()
    const start = now()
    const timeout = controller
        ? setTimeout(() => controller.abort(), timeoutMs)
        : null

    try {
        const response = await fetchImpl(endpoint, {
            method: 'GET',
            cache: 'no-store',
            signal: controller?.signal,
        })
        if (!response.ok) return null
        return Math.max(0, Math.round(now() - start))
    } catch {
        return null
    } finally {
        if (timeout) clearTimeout(timeout)
    }
}

export function getLiveKitConnectTimeoutMs(
    connectOptions?: Partial<ConnectOptions>
): number {
    const peerConnectionTimeout = connectOptions?.peerConnectionTimeout ?? 0
    const websocketTimeout = connectOptions?.websocketTimeout ?? 0

    return Math.max(30000, peerConnectionTimeout + 5000, websocketTimeout + 5000)
}

export function calculateReconnectDelayMs(input: ReconnectDelayInput): number {
    const attempt = Math.max(0, input.attempt)
    const baseDelayMs = Math.max(0, input.baseDelayMs)
    const maxDelayMs = input.maxDelayMs ?? 30000
    const jitterRatio = Math.max(0, input.jitterRatio ?? 0.2)
    const random = input.random ?? Math.random

    const exponentialDelay = Math.min(maxDelayMs, baseDelayMs * Math.pow(2, attempt))
    const jitterMs = Math.round(exponentialDelay * jitterRatio * random())

    return Math.min(maxDelayMs, exponentialDelay + jitterMs)
}

export function isBrowserOffline(navigatorLike?: { onLine?: boolean } | null): boolean {
    return navigatorLike?.onLine === false
}

export function selectStartupMediaPlan(input: StartupMediaPlanInput): StartupMediaPlan {
    if (!input.userPrefersVideo) {
        return {
            keepVideoSuppressedUntilManual: false,
            shouldDelayCameraStart: false,
            cameraEnableDelayMs: 0,
            publishRetryDelaysMs: DEFAULT_PUBLISH_RETRY_DELAYS_MS,
        }
    }

    if (input.connectionProfile.startWithVideoDisabled) {
        return {
            keepVideoSuppressedUntilManual: true,
            shouldDelayCameraStart: false,
            cameraEnableDelayMs: 0,
            publishRetryDelaysMs: LONG_HAUL_PUBLISH_RETRY_DELAYS_MS,
        }
    }

    if ((input.measuredRttMs ?? 0) >= LONG_HAUL_AUDIO_FIRST_RTT_MS) {
        return {
            keepVideoSuppressedUntilManual: false,
            shouldDelayCameraStart: true,
            cameraEnableDelayMs: LONG_HAUL_CAMERA_DELAY_MS,
            publishRetryDelaysMs: LONG_HAUL_PUBLISH_RETRY_DELAYS_MS,
        }
    }

    if (input.connectionProfile.key === 'low-bandwidth') {
        return {
            keepVideoSuppressedUntilManual: false,
            shouldDelayCameraStart: true,
            cameraEnableDelayMs: LOW_BANDWIDTH_CAMERA_DELAY_MS,
            publishRetryDelaysMs: LOW_BANDWIDTH_PUBLISH_RETRY_DELAYS_MS,
        }
    }

    return {
        keepVideoSuppressedUntilManual: false,
        shouldDelayCameraStart: false,
        cameraEnableDelayMs: 0,
        publishRetryDelaysMs: DEFAULT_PUBLISH_RETRY_DELAYS_MS,
    }
}

export function decideAdaptiveCameraPlan(input: AdaptiveCameraPlanInput): AdaptiveCameraPlan {
    const isPoor = input.quality === 'poor' || input.quality === 'unknown'
    const poorOrUnknownStreak = isPoor ? input.poorOrUnknownStreak + 1 : 0

    return {
        poorOrUnknownStreak,
        constraints: getAdaptiveQualityConstraints(input.quality),
        shouldDisableVideo:
            isPoor &&
            poorOrUnknownStreak >= 3 &&
            input.userPrefersVideo &&
            input.isVideoCurrentlyEnabled &&
            !input.networkVideoSuppressed &&
            !input.isScreenShareEnabled,
    }
}

export function toMediaTrackConstraints(constraints: VideoConstraints): MediaTrackConstraints {
    return {
        width: { ideal: constraints.width },
        height: { ideal: constraints.height },
        frameRate: { ideal: constraints.frameRate },
    }
}

export function resolveMediaStreamTrack(
    trackLike:
        | {
            mediaStreamTrack?: MediaStreamTrack
            getSettings?: MediaStreamTrack['getSettings']
            applyConstraints?: MediaStreamTrack['applyConstraints']
        }
        | null
        | undefined
): MediaStreamTrack | null {
    if (!trackLike) return null

    if (typeof trackLike.getSettings === 'function' && typeof trackLike.applyConstraints === 'function') {
        return trackLike as MediaStreamTrack
    }

    return trackLike.mediaStreamTrack ?? null
}
